import { useState, useCallback, useMemo } from 'react';
import { SortingState, PaginationState } from '@tanstack/react-table';

export interface UseTableStateOptions {
  /** Initial sorting state */
  initialSorting?: SortingState;
  /** Enable pagination */
  enablePagination?: boolean;
  /** Initial page size (default: 25) */
  initialPageSize?: number;
  /** Initial page index (0-based for TanStack, default: 0) */
  initialPageIndex?: number;
  /** Enable row selection tracking */
  enableSelection?: boolean;
  /** Initial selected row IDs */
  initialSelectedRows?: (string | number)[];
  /** Callback when pagination state changes (page or size) */
  onPaginationChange?: (pagination: PaginationState) => void;
  /** Callback when sorting changes */
  onSortingChange?: (sorting: SortingState) => void;
  /** Callback when selection changes */
  onSelectionChange?: (selectedIds: (string | number)[]) => void;
}

export interface TableState {
  // Sorting
  sorting: SortingState;
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>;

  // Pagination
  pagination: PaginationState;
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>;
  pageIndex: number;
  pageSize: number;
  setPageIndex: (pageIndex: number) => void;
  setPageSize: (pageSize: number) => void;
  goToFirstPage: () => void;
  goToLastPage: (totalPages: number) => void;
  goToNextPage: () => void;
  goToPreviousPage: () => void;

  // Selection
  selectedRows: (string | number)[];
  setSelectedRows: React.Dispatch<React.SetStateAction<(string | number)[]>>;
  clearSelection: () => void;

  // Hover
  hoveredRowId: string | number | null;
  setHoveredRowId: (id: string | number | null) => void;

  // Reset
  resetState: () => void;
}

/**
 * Custom hook to manage comprehensive table state including:
 * - Sorting
 * - Pagination
 * - Row selection
 * - Row hover state
 *
 * @example
 * ```tsx
 * const tableState = useTableState({
 *   enablePagination: true,
 *   initialPageSize: 25,
 *   onPageChange: (page) => console.log('Page changed to:', page),
 *   onSortingChange: (sorting) => console.log('Sorting changed:', sorting),
 * });
 *
 * <UPTanStackTable
 *   data={data}
 *   columns={columns}
 *   sorting={tableState.sorting}
 *   onSortingChange={tableState.setSorting}
 *   pagination={tableState.pagination}
 *   onPaginationChange={tableState.setPagination}
 * />
 * ```
 */
export const useTableState = (options: UseTableStateOptions = {}): TableState => {
  const {
    initialSorting = [],
    initialPageSize = 25,
    initialPageIndex = 0,
    initialSelectedRows = [],
    onPaginationChange,
    onSortingChange,
    onSelectionChange,
  } = options;

  // Sorting state
  const [sorting, setSortingState] = useState<SortingState>(initialSorting);

  // Pagination state - TanStack uses 0-based indexing
  const [pagination, setPaginationState] = useState<PaginationState>({
    pageIndex: initialPageIndex,
    pageSize: initialPageSize,
  });

  // Selection state
  const [selectedRows, setSelectedRowsState] = useState<(string | number)[]>(initialSelectedRows);

  // Hover state
  const [hoveredRowId, setHoveredRowIdState] = useState<string | number | null>(null);

  // Sorting handlers
  const setSorting = useCallback(
    (updater: React.SetStateAction<SortingState>) => {
      setSortingState(prev => {
        const newSorting = typeof updater === 'function' ? updater(prev) : updater;
        onSortingChange?.(newSorting);
        return newSorting;
      });
    },
    [onSortingChange],
  );

  // Pagination handlers - simple, just call the callback with new state
  const setPagination = useCallback(
    (updater: React.SetStateAction<PaginationState>) => {
      setPaginationState(prev => {
        const newPagination = typeof updater === 'function' ? updater(prev) : updater;
        onPaginationChange?.(newPagination);
        return newPagination;
      });
    },
    [onPaginationChange],
  );

  const setPageIndex = useCallback(
    (pageIndex: number) => {
      setPagination(prev => ({ ...prev, pageIndex }));
    },
    [setPagination],
  );

  const setPageSize = useCallback(
    (pageSize: number) => {
      setPagination(prev => ({ ...prev, pageSize, pageIndex: 0 })); // Reset to first page (0-based)
    },
    [setPagination],
  );

  const goToFirstPage = useCallback(() => {
    setPageIndex(0); // TanStack uses 0-based indexing
  }, [setPageIndex]);

  const goToLastPage = useCallback(
    (totalPages: number) => {
      setPageIndex(Math.max(0, totalPages - 1)); // TanStack uses 0-based indexing
    },
    [setPageIndex],
  );

  const goToNextPage = useCallback(() => {
    setPagination(prev => ({ ...prev, pageIndex: prev.pageIndex + 1 }));
  }, [setPagination]);

  const goToPreviousPage = useCallback(() => {
    setPagination(prev => ({ ...prev, pageIndex: Math.max(0, prev.pageIndex - 1) }));
  }, [setPagination]);

  // Selection handlers
  const setSelectedRows = useCallback(
    (updater: React.SetStateAction<(string | number)[]>) => {
      setSelectedRowsState(prev => {
        const newSelection = typeof updater === 'function' ? updater(prev) : updater;
        onSelectionChange?.(newSelection);
        return newSelection;
      });
    },
    [onSelectionChange],
  );

  const clearSelection = useCallback(() => {
    setSelectedRows([]);
  }, [setSelectedRows]);

  // Hover handlers
  const setHoveredRowId = useCallback((id: string | number | null) => {
    setHoveredRowIdState(id);
  }, []);

  // Reset all state
  const resetState = useCallback(() => {
    setSortingState(initialSorting);
    setPaginationState({
      pageIndex: initialPageIndex,
      pageSize: initialPageSize,
    });
    setSelectedRowsState(initialSelectedRows);
    setHoveredRowIdState(null);
  }, [initialSorting, initialPageIndex, initialPageSize, initialSelectedRows]);

  // Memoized state object
  return useMemo(
    () => ({
      // Sorting
      sorting,
      setSorting,

      // Pagination
      pagination,
      setPagination,
      pageIndex: pagination.pageIndex,
      pageSize: pagination.pageSize,
      setPageIndex,
      setPageSize,
      goToFirstPage,
      goToLastPage,
      goToNextPage,
      goToPreviousPage,

      // Selection
      selectedRows,
      setSelectedRows,
      clearSelection,

      // Hover
      hoveredRowId,
      setHoveredRowId,

      // Reset
      resetState,
    }),
    [
      sorting,
      setSorting,
      pagination,
      setPagination,
      setPageIndex,
      setPageSize,
      goToFirstPage,
      goToLastPage,
      goToNextPage,
      goToPreviousPage,
      selectedRows,
      setSelectedRows,
      clearSelection,
      hoveredRowId,
      setHoveredRowId,
      resetState,
    ],
  );
};
